/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useCallback, type ReactNode } from 'react';
import { useAuth } from '@/features/Auth/state/AuthContext';
import { isFeatureFlagEnabled } from '@/infrastructure/config/featureFlags';
import { useMeContext } from '@/shared/hooks/api/useMeContext';

interface FeatureFlagsContextType {
  isEnabled: (name: string) => boolean;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(undefined);

/**
 * Feature flags vêm SÓ do env do web (`isFeatureFlagEnabled`) — não há mais
 * consulta ao DB nem sync em runtime. O provider continua existindo apenas para
 * expor `isEnabled` e ancorar o `MeContextBootstrap` dentro da árvore.
 */
export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const isEnabled = useCallback((name: string) => isFeatureFlagEnabled(name), []);

  return (
    <FeatureFlagsContext.Provider value={{ isEnabled }}>
      <MeContextBootstrap>{children}</MeContextBootstrap>
    </FeatureFlagsContext.Provider>
  );
}

/**
 * Bootstrap component — fires GET /auth/me/context once when authenticated.
 * Must live INSIDE FeatureFlagsContext.Provider so
 * useMeContext's useFeatureFlags() resolves the context. Renders children
 * untouched.
 */
function MeContextBootstrap({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  useMeContext({ enabled: isAuthenticated });
  return <>{children}</>;
}

export function useFeatureFlags() {
  const context = useContext(FeatureFlagsContext);
  if (context === undefined) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }
  return context;
}
